import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

import { NotConnected, useServer } from "@/components/server-hooks";
import { CITY, Panel, ServerPage } from "@/components/server-shell";
import { ago, Chip, errorMessage } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";
import { useOpenPlane } from "@/lib/plane";
import { type RunRow, type RunsList, when } from "@/lib/runs";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/$id/")({
  component: Today,
});

type Proposal = {
  id: string;
  op: string;
  project: string | null;
  plane_issue: string | null;
  title: string;
  reason: string;
  priority: string | null;
  target_date: string | null;
};
type Question = { line: string; text: string; answers: string[] };
type Questions = { open: Question[]; answered: Question[]; pending: string[] };
type ClaudeStatus = {
  auth_mode: "subscription" | "api_key";
  cost: { days: number; usd: number; runs: number };
};

const OP_LABEL: Record<string, string> = {
  create: "New task",
  update: "Update",
  comment: "Comment",
  reprioritize: "Change priority",
  review: "Move to In Review",
  close: "Close",
};
/** Short labels for the activity feed's chips. */
const KIND_SHORT: Record<string, string> = {
  suggest: "cycle",
  apply: "cycle",
  sweep: "sweep",
  catchup: "catch-up",
  backfill: "import",
  approved: "approved",
};
const TIMERS = [
  "task-manager-ai",
  "task-manager-ai-sweep",
  "task-manager-ai-briefing",
  "task-manager-ai-health",
  "task-manager-ai-context-commit",
  "task-manager-ai-inbox-scan",
];
const gb = (n?: number) => (n ? `${Math.round(n / 1024 ** 3)} GB` : "?");
const due = (d: string) =>
  new Date(`${d}T00:00`).toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
const isCycle = (r: RunRow) => r.mode === "suggest" || r.mode === "apply";
const taskKey = (k: string | null) => (k && /^[A-Z][A-Z0-9]*-\d+$/.test(k) ? k : null);

function Today() {
  const { id } = Route.useParams();
  const q = useServer(id);
  const connected = !!q.data?.hub.connected;
  const st = q.data?.hub.status;
  const proposals = useAgentQuery<{
    pending: Proposal[];
    mode: string;
    applying: boolean;
  }>(id, "proposals_list", {}, connected);
  const runs = useAgentQuery<RunsList>(id, "runs_list", {}, connected);
  const questions = useAgentQuery<Questions>(id, "questions_list", {}, connected);
  const claude = useAgentQuery<ClaudeStatus>(id, "claude_status", {}, connected);
  const plane = useOpenPlane(id);
  const runCycle = useMutation({
    mutationFn: () => client.servers.runCycle({ id }),
    onSuccess: () => toast.success("Cycle started"),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const decide = useAgentAction<{ ids: string[]; decision: "approve" | "dismiss" }, { applying?: number; dismissed?: number }>(
    id,
    "proposals_decide",
    (v) => v,
    {
      refresh: ["proposals_list"],
      success: (r) => (r.applying ? `Applying ${r.applying} in Plane; this takes a minute or two` : `Dismissed ${r.dismissed}`),
    },
  );
  const base = `/servers/${id}`;
  const dateLine = new Date().toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  if (!q.data) return <ServerPage title="Today">Loading…</ServerPage>;
  const install = st?.install;
  const srv = q.data.server;
  if (srv.status === "creating" || srv.status === "booting" || (install && install.state !== "installed" && install.state !== "unknown"))
    return <SettingUp id={id} />;
  const collectors = (st?.collectors ?? []).filter((c) => c.source);
  const failing = collectors.filter((c) => Number(c.failures ?? 0) > 0);
  const pending = proposals.data?.pending ?? [];
  const mode = proposals.data?.mode;
  const allRuns = runs.data?.runs ?? [];
  const lastCycle = allRuns.find(isCycle);
  const today = new Date().toDateString();
  const todays = allRuns.filter((r) => new Date(r.started_at).toDateString() === today);
  const notable = todays.filter((r) => r.proposed + r.applied + r.refused > 0 || r.status !== "ok" || !isCycle(r));
  const openQs = (questions.data?.open ?? []).filter((x) => !questions.data?.pending.includes(x.line));
  const timers = TIMERS.map((t) => st?.units?.[`${t}.timer`]).filter((t) => !!t);
  const timersOn = timers.filter((t) => t.ActiveState === "active").length;
  const stats = runs.data?.stats;

  return (
    <ServerPage
      title="Today"
      subtitle={[dateLine, lastCycle && `last cycle ${ago(lastCycle.started_at)}`].filter(Boolean).join(" · ")}
      actions={
        <>
          {mode && (
            <Link
              to="/servers/$id/claude"
              params={{ id }}
              className={cn(
                "self-center rounded-[4px] px-2.5 py-1.5 font-mono text-xs no-underline",
                mode === "suggest" ? "bg-[rgba(232,163,61,0.14)] text-[#F0B65A]" : "bg-[rgba(95,191,127,0.14)] text-[#7FD39B]",
              )}
            >
              {mode === "suggest" ? "Suggest mode" : "Apply mode"}
            </Link>
          )}
          <Button variant="outline" disabled={!connected || runCycle.isPending} onClick={() => runCycle.mutate()}>
            Run a cycle now
          </Button>
          <Button variant="outline" disabled={!connected || plane.isPending} onClick={() => plane.mutate(undefined)}>
            Open Plane ↗
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {!connected && <NotConnected />}

        <section
          aria-label="Last 24 hours"
          className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-px overflow-hidden rounded-md bg-[#2A2927]"
        >
          <Stat n={stats?.runs} label="runs in the last 24 h" to={`${base}/activity`} />
          <Stat n={stats?.changes} label="task changes proposed or made · 24 h" to={`${base}/activity`} />
          <Stat
            n={proposals.data ? pending.length : undefined}
            label="suggestions waiting"
            hot={pending.length > 0}
            to={`${base}/suggestions`}
          />
          <Stat n={stats?.failed} label="failed runs · 24 h" bad={!!stats?.failed} to={`${base}/activity`} />
        </section>

        <div className="flex flex-wrap items-start gap-6">
          <div className="flex min-w-0 flex-[2_1_560px] flex-col gap-6">
            {pending.length > 0 && (
              <Box
                title={
                  <>
                    Suggestions waiting <span className="text-[#FF4F1A]">{pending.length}</span>
                  </>
                }
                aside={
                  <Link to="/servers/$id/suggestions" params={{ id }} className="font-mono text-[13px] no-underline">
                    Review all →
                  </Link>
                }
              >
                {pending.slice(0, 3).map((p) => {
                  const key = taskKey(p.plane_issue);
                  const project = p.project && p.project.length <= 20 ? p.project : null;
                  const meta = [project, p.target_date && `due ${due(p.target_date)}`, p.priority, p.title ? p.reason : null].filter(
                    Boolean,
                  );
                  return (
                    <div key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-t border-[#2A2927] px-[22px] py-4">
                      <Chip value="info" label={OP_LABEL[p.op] ?? p.op} />
                      <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-0.5">
                        <span className="font-medium">
                          {key && (
                            <>
                              <Link to="/servers/$id/tasks/$key" params={{ id, key }} className="font-mono">
                                {key}
                              </Link>{" "}
                              ·{" "}
                            </>
                          )}
                          {p.title || p.reason}
                        </span>
                        {meta.length > 0 && <span className="text-[13px] text-[#A8A59D]">{meta.join(" · ")}</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          disabled={decide.isPending || !!proposals.data?.applying}
                          onClick={() => decide.mutate({ ids: [p.id], decision: "approve" })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ ids: [p.id], decision: "dismiss" })}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </Box>
            )}

            {openQs.length > 0 && (
              <Box
                title={
                  <>
                    The agent has a question <span className="text-[#A8A59D]">1 of {openQs.length}</span>
                  </>
                }
                aside={
                  <Link to="/servers/$id/questions" params={{ id }} className="font-mono text-[13px] no-underline">
                    All questions →
                  </Link>
                }
              >
                <Ask key={openQs[0].line} id={id} q={openQs[0]} />
              </Box>
            )}

            <section aria-labelledby="t-since" className="flex flex-col">
              <h2 id="t-since" className="m-0 mb-3 font-mono text-[15px] font-bold">
                Since this morning
              </h2>
              {notable.length > 0 && (
                <div className="flex flex-col border-b border-[#262523]">
                  {notable.slice(0, 8).map((r) => (
                    <Link
                      key={r.id}
                      to="/servers/$id/runs/$rid"
                      params={{ id, rid: String(r.id) }}
                      className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1.5 border-t border-[#262523] py-3 text-[#ECEAE4] no-underline hover:text-[#ECEAE4]"
                    >
                      <span className="w-[52px] font-mono text-[13px] text-[#A8A59D]">{when(r.started_at)}</span>
                      <Chip
                        value={r.status === "failed" ? "error" : r.status === "running" ? "running" : "neutral"}
                        label={r.status === "failed" ? `${KIND_SHORT[r.mode] ?? r.mode} failed` : (KIND_SHORT[r.mode] ?? r.mode)}
                      />
                      <span className="flex-[1_1_300px]">
                        {r.summary || (r.status === "running" ? "Running…" : `Run ${r.id}`)}
                        {r.proposed + r.applied > 0 && (
                          <span className="text-[#A8A59D]">
                            {" "}
                            · {[r.proposed && `${r.proposed} proposed`, r.applied && `${r.applied} applied`].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
              {runs.data && notable.length === 0 && (
                <p className="m-0 border-y border-[#262523] py-3 text-sm text-[#A8A59D]">
                  {todays.length
                    ? `Quiet so far: ${todays.length} cycle${todays.length > 1 ? "s" : ""} ran today with nothing to change.`
                    : "No runs yet today."}
                </p>
              )}
              <Link to="/servers/$id/activity" params={{ id }} className="mt-2 self-start font-mono text-[13px] no-underline">
                All activity →
              </Link>
            </section>
          </div>

          <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-6">
            <section
              aria-labelledby="t-health"
              className="flex flex-col gap-1 rounded-md bg-[#1A1A19] px-[22px] py-[18px] shadow-[inset_0_0_0_1px_#2A2927]"
            >
              <h2 id="t-health" className="m-0 mb-2.5 font-mono text-[15px] font-bold">
                Health
              </h2>
              <HealthRow label="Sources">
                {collectors.length ? (
                  <span className={failing.length ? "text-[#F48A80]" : "text-[#7FD39B]"}>
                    {failing.length
                      ? `${failing.length} of ${collectors.length} failing`
                      : `${collectors.length} of ${collectors.length} reading`}
                  </span>
                ) : (
                  <span className="text-[#A8A59D]">none yet</span>
                )}
              </HealthRow>
              {failing.map((c) => (
                <span
                  key={`${c.source}:${c.account}`}
                  className="truncate pb-1.5 text-[13px] text-[#A8A59D]"
                  title={String(c.last_error ?? "")}
                >
                  {String(c.source)} · {String(c.account)}: {String(c.last_error ?? `${c.failures} failures`)}
                </span>
              ))}
              <HealthRow label="Last cycle">
                {lastCycle ? (
                  <span
                    className={
                      lastCycle.status === "failed" ? "text-[#F48A80]" : lastCycle.status === "ok" ? "text-[#7FD39B]" : "text-[#F0B65A]"
                    }
                  >
                    {lastCycle.status} · run {lastCycle.id.toLocaleString()}
                  </span>
                ) : (
                  <span className="text-[#A8A59D]">–</span>
                )}
              </HealthRow>
              <HealthRow label="Schedules">
                {timers.length ? (
                  <span className={timersOn === timers.length ? "text-[#7FD39B]" : "text-[#F0B65A]"}>
                    {timersOn} of {timers.length} on
                  </span>
                ) : (
                  <span className="text-[#A8A59D]">–</span>
                )}
              </HealthRow>
              <HealthRow label="Disk">{st?.disk ? `${gb(st.disk.free)} free` : "?"}</HealthRow>
              {claude.data?.cost && (
                <HealthRow label={`AI · last ${claude.data.cost.days} days`}>
                  ≈ ${claude.data.cost.usd.toFixed(2)} · {claude.data.auth_mode === "subscription" ? "subscription" : "API key"}
                </HealthRow>
              )}
              <Link to="/servers/$id/server" params={{ id }} className="mt-2 font-mono text-[13px] no-underline">
                Server details →
              </Link>
            </section>

            {mode === "suggest" && (
              <section
                aria-labelledby="t-mode"
                className="flex flex-col gap-2 rounded-md bg-[rgba(232,163,61,0.08)] px-[22px] py-[18px] shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]"
              >
                <h2 id="t-mode" className="m-0 font-mono text-sm font-bold text-[#F0B65A]">
                  You’re in suggest mode
                </h2>
                <p className="m-0 text-[13px] text-[#CFCBC1]">
                  Fieldwork proposes every change and waits for your approval. Switch to Apply once its suggestions look right.
                </p>
                <Link to="/servers/$id/claude" params={{ id }} className="font-mono text-[13px] no-underline">
                  Change in AI settings →
                </Link>
              </section>
            )}
          </aside>
        </div>
      </div>
    </ServerPage>
  );
}

/** A card with a titled header row and divided rows, as on the canvas. */
function Box({ title, aside, children }: { title: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
      <div className="flex items-center justify-between gap-3 px-[22px] py-4">
        <h2 className="m-0 font-mono text-[15px] font-bold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function HealthRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-2 border-t border-[#262523] py-[9px]">
      <span>{label}</span>
      <span className="text-right font-mono text-[13px]">{children}</span>
    </div>
  );
}

function Ask({ id, q }: { id: string; q: Question }) {
  const [text, setText] = useState("");
  const answer = useAgentAction<{ text?: string; ignore?: boolean }>(id, "question_answer", (v) => ({ ...v, question: q.line }), {
    refresh: ["questions_list", "people_list"],
    success: "Saved; the agent uses it on its next run",
  });
  const who = /^who\b/i.test(q.text);
  return (
    <form
      className="flex flex-col gap-3 border-t border-[#2A2927] px-[22px] py-[18px]"
      onSubmit={(e) => {
        e.preventDefault();
        answer.mutate({ text });
      }}
    >
      <p className="m-0 text-[15px]">{q.text}</p>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{who ? "Who they are" : "Your answer"}</span>
        <Input
          className="h-11 bg-[#111111]"
          maxLength={400}
          placeholder={who ? "Name, role and company" : "Your answer"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          required
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={answer.isPending}>
          Answer
        </Button>
        <Button type="button" variant="outline" disabled={answer.isPending} onClick={() => answer.mutate({ ignore: true })}>
          Not relevant, ignore
        </Button>
      </div>
    </form>
  );
}

function Stat({ n, label, to, hot, bad }: { n?: number; label: string; to: string; hot?: boolean; bad?: boolean }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 bg-[#1A1A19] px-[22px] py-[18px] text-[#ECEAE4] no-underline hover:bg-[#222220] hover:text-[#ECEAE4]"
    >
      <span className={cn("font-mono text-[26px] leading-tight font-bold", hot && "text-[#FF4F1A]", bad && "text-[#F48A80]")}>
        {n?.toLocaleString() ?? "–"}
      </span>
      <span className="text-[13px] text-[#A8A59D]">{label}</span>
    </Link>
  );
}

/** While a new server is being created and installed (SettingUp on the canvas). */
function SettingUp({ id }: { id: string }) {
  const q = useServer(id);
  const s = q.data!.server;
  const connected = !!q.data!.hub.connected;
  const install = q.data!.hub.status?.install;
  const failed = install?.state === "failed" || s.status === "error";
  const created = s.status !== "creating";
  const started = connected || !!install;
  const done = install?.state === "installed";
  const at = !created ? 0 : !started ? 1 : !done ? 2 : 3;
  const hetzner = s.provider === "hetzner";
  const steps: { title: string; detail: ReactNode }[] = [
    {
      title: hetzner ? "Created in your Hetzner project" : "Added to the panel",
      detail: hetzner
        ? `${s.serverType ?? ""} in ${CITY[s.location ?? ""] ?? s.location ?? ""}, with a firewall that allows no inbound connections · ${new Date(s.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
        : "Waiting for the agent on your machine",
    },
    { title: "Started and connected to this panel", detail: "The server connects out; it opens no ports" },
    {
      title: "Installing Fieldwork and Plane",
      detail: failed ? <span className="text-[#F48A80]">Stopped{install?.step ? ` at “${install.step}”` : ""}</span> : (install?.step ?? "Waiting for the first report"),
    },
    { title: "Ready to connect your sources", detail: "Then the first 15-minute cycle runs" },
  ];
  const pct = Math.round(((at + (failed ? 0 : 0.5)) / steps.length) * 100);
  return (
    <ServerPage
      narrow
      title={
        <>
          {failed ? "Setting up failed:" : "Setting up"} {s.name}{" "}
          <Chip value={failed ? "failed" : "info"} label={failed ? "failed" : s.status === "creating" ? "creating" : "installing"} />
        </>
      }
      subtitle={
        failed
          ? "The install stopped. The log below shows where; you can delete the server (Server page) and create it again."
          : "Usually takes about 10 minutes. You can close this page; setup carries on, and we email you when it's ready."
      }
    >
      {s.error && (
        <p className="m-0 rounded-md bg-[rgba(240,100,90,0.08)] px-4 py-3 text-sm text-[#F48A80] shadow-[inset_0_0_0_1px_rgba(240,100,90,0.35)]">
          {s.error}
        </p>
      )}
      <div aria-label="Progress" className="flex flex-col gap-2">
        <div className="flex justify-between font-mono text-[13px]">
          <span>{steps[Math.min(at, 3)]!.title}</span>
          <span className="text-[#A8A59D]">
            step {Math.min(at + 1, 4)} of {steps.length}
          </span>
        </div>
        <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="h-1.5 rounded-[3px] bg-[#2A2927]">
          <div className={cn("h-1.5 rounded-[3px]", failed ? "bg-[#F48A80]" : "bg-[#FF4F1A]")} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <ol className="m-0 flex list-none flex-col rounded-md bg-[#1A1A19] p-0 shadow-[inset_0_0_0_1px_#2A2927] [&>li+li]:border-t [&>li+li]:border-[#2A2927]">
        {steps.map((st, i) => (
          <li
            key={st.title}
            aria-current={i === at ? "step" : undefined}
            className={cn("flex gap-3.5 px-[22px] py-4", i === at && "bg-[#1F1F1D]", i > at && "text-[#A8A59D]")}
          >
            {i < at ? (
              <span aria-hidden="true" className="grid size-[22px] shrink-0 place-items-center rounded-full bg-[rgba(95,191,127,0.18)] text-[13px] text-[#7FD39B]">
                ✓
              </span>
            ) : i === at ? (
              <span aria-hidden="true" className={cn("grid size-[22px] shrink-0 place-items-center rounded-full", failed ? "shadow-[inset_0_0_0_2px_#F48A80]" : "shadow-[inset_0_0_0_2px_#FF4F1A]")}>
                <span className={cn("size-2 rounded-full", failed ? "bg-[#F48A80]" : "bg-[#FF4F1A]")} />
              </span>
            ) : (
              <span aria-hidden="true" className="size-[22px] shrink-0 rounded-full shadow-[inset_0_0_0_1.5px_#3A3935]" />
            )}
            <span className="flex flex-col gap-0.5">
              <span className="font-medium">{st.title}</span>
              <span className="text-[13px] text-[#A8A59D]">{st.detail}</span>
            </span>
          </li>
        ))}
      </ol>

      {install?.tail && install.tail.length > 0 && (
        <details open className="rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
          <summary className="cursor-pointer px-[22px] py-3.5 font-mono text-[13px]">Install log</summary>
          <pre className="m-0 max-h-[200px] overflow-auto px-[22px] pb-[18px] font-mono text-xs leading-[1.7] whitespace-pre-wrap text-[#CFCBC1]">
            {install.tail.join("\n")}
          </pre>
        </details>
      )}

      {!failed && (
        <section
          aria-labelledby="su-next"
          className="flex flex-col gap-2.5 rounded-md bg-[rgba(255,79,26,0.06)] px-[22px] py-5 shadow-[inset_0_0_0_1px_rgba(255,79,26,0.35)]"
        >
          <h2 id="su-next" className="m-0 font-mono text-[15px] font-bold">
            While you wait
          </h2>
          <p className="m-0 text-[#CFCBC1]">
            Get your keys ready. Next you'll connect Gmail (with your own Google client), Slack, Notion, GitHub and Telegram, and
            choose how the agent pays for Claude.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to="/servers/$id/sources" params={{ id }} className="inline-flex min-h-10 items-center rounded-[4px] px-3.5 font-mono text-[13px] no-underline shadow-[inset_0_0_0_1px_#3A3935]">
              What each source needs →
            </Link>
            <Link to="/servers/$id/claude" params={{ id }} className="inline-flex min-h-10 items-center rounded-[4px] px-3.5 font-mono text-[13px] no-underline shadow-[inset_0_0_0_1px_#3A3935]">
              Subscription or API key? →
            </Link>
          </div>
        </section>
      )}
      {failed && (
        <Panel title="What next">
          <p className="m-0 text-[#CFCBC1]">
            Open the Server page for the full install log. Deleting the server there and creating it again usually fixes a failed
            first boot.
          </p>
          <Link to="/servers/$id/server" params={{ id }} className="self-start font-mono text-[13px]">
            Server page →
          </Link>
        </Panel>
      )}
    </ServerPage>
  );
}
